import { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import * as EventEmitter from '@/utils/event-emitter';
import { TTabOption } from './tabs.types';

const useTabsEmitters = ({
  id,
  activeIndex,
  tabs,
  setActiveIndex,
}: {
  id: string;
  activeIndex: number;
  tabs: TTabOption[];
  setActiveIndex: Dispatch<SetStateAction<number>>;
}) => {
  // Keep stable refs so event handlers always see the latest values
  // without needing to re-register on every render
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  // broadcast current active tab whenever the active index changes
  useEffect(() => {
    const activeTab = tabsRef.current[activeIndex];
    if (!activeTab) return;
    EventEmitter.broadcastEvent(`${id}::tabs-status`, { activeIndex, activeTab });
  }, [id, activeIndex]);

  // respond to on-demand status requests (e.g. from useTabs on mount)
  useEffect(() => {
    const handleRequestStatus = () => {
      const idx = activeIndexRef.current;
      const activeTab = tabsRef.current[idx];
      if (!activeTab) return;
      EventEmitter.broadcastEvent(`${id}::tabs-status`, { activeIndex: idx, activeTab });
    };

    EventEmitter.addEventListener(`${id}::tabs-request-status`, handleRequestStatus);
    return () => EventEmitter.removeEventListener(`${id}::tabs-request-status`, handleRequestStatus);
  }, [id]);

  // listen for set-index and set-value commands
  useEffect(() => {
    const handleSetIndex = (index: number) => {
      if (index >= 0 && index < tabsRef.current.length) {
        setActiveIndex(index);
      }
    };

    const handleSetValue = (value: string) => {
      const index = tabsRef.current.findIndex((tab) => tab.value === value);
      if (index !== -1) {
        setActiveIndex(index);
      }
    };

    EventEmitter.addEventListener(`${id}::tabs-set-index`, handleSetIndex);
    EventEmitter.addEventListener(`${id}::tabs-set-value`, handleSetValue);

    return () => {
      EventEmitter.removeEventListener(`${id}::tabs-set-index`, handleSetIndex);
      EventEmitter.removeEventListener(`${id}::tabs-set-value`, handleSetValue);
    };
  }, [id, setActiveIndex]);
};

export default useTabsEmitters;
